import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useClientHistory, { JOIN, RESULT, UPLOAD } from 'client/hooks/useClientHistory';
import { css } from '@emotion/core';
import WaveAnimationText from 'components/animation/WaveAnimationText';
import LottieFile from 'components/animation/LottieFile';
import TextBoxClient from 'client/components/TextBoxClient';
import useGetEntryRecordResult, {
    hasGetEntryRecordResultPromoType
} from 'client/hooks/useGetEntryRecordResult';
import { LOTTIE_FILE_URL } from 'assets/config';
import i18next from 'i18next';
import useAIJobs from 'client/hooks/useAIJobs';
import { isNoResultPageGames } from 'config/promoGameTypesClientConfig';
import PureDialog from 'client/components/dialog/PureDialog';
import { setSessionStorage } from 'utils/setSessionStorage';

export default function TransitionPage() {
    const history = useClientHistory();
    const { hasGameResult } = useSelector(s => s.userEntryState);
    const { currentEntryUUID: entryUUID } = useSelector(s => s.userEntryState);
    const { uuid: promoUUID, type: promoType } = useSelector(s => s.promo);
    const { getEntryRecordResult } = useGetEntryRecordResult();
    const [isEntryRecordResultInitialized, setIsEntryRecordResultInitialized] = useState(false);
    const [isShowRetryDialog, setIsShowRetryDialog] = useState(false);
    const lastEntryResultRecord = useRef(null);
    const isInitialized = useRef(false);
    const { onRedirect, onAIJobsCheck } = useAIJobs({
        setIsShowRetryDialog
    });

    const handleRetryButtonClick = useCallback(() => {
        setIsShowRetryDialog(false);
        if (promoUUID && entryUUID) onAIJobsCheck(promoUUID, entryUUID, true);
    }, [promoUUID, entryUUID, onAIJobsCheck]);

    const {
        enableLoadingImage,
        loadingImageUrl,
        enableAnimationHintText,
        animationHintText,
        enableTransitionContent,
        transitionContent,
        titleColor
    } = useSelector(s => s.settingStyle);

    const isLottieFileUrl = loadingImageUrl && loadingImageUrl.toLowerCase().endsWith('.json');
    const isDefaultLottieFile = loadingImageUrl === LOTTIE_FILE_URL.transitionLoading;

    useEffect(() => {
        const init = async () => {
            if (!hasGameResult) return history.push(JOIN);
            if (hasGetEntryRecordResultPromoType(promoType)) {
                const result = await getEntryRecordResult();
                lastEntryResultRecord.current = result;
            }
            setIsEntryRecordResultInitialized(true);
        };
        if (!isEntryRecordResultInitialized) init();
    }, [
        entryUUID,
        getEntryRecordResult,
        hasGameResult,
        history,
        isEntryRecordResultInitialized,
        promoType,
        promoUUID
    ]);

    useEffect(() => {
        const init = async () => {
            if (
                isEntryRecordResultInitialized &&
                Boolean(promoUUID) &&
                Boolean(entryUUID) &&
                !isInitialized.current
            ) {
                if (hasGetEntryRecordResultPromoType(promoType)) {
                    const {
                        isAIFlowFinished,
                        isUploadFlowFinished
                    } = lastEntryResultRecord.current;
                    const hasFinished = isAIFlowFinished && isUploadFlowFinished && hasGameResult;
                    if (hasFinished) {
                        setSessionStorage({
                            key: 'isTransitionPageFinished',
                            value: entryUUID,
                            isNoJSONStringify: true
                        });
                        onRedirect({
                            redirectTarget: RESULT
                        });
                    } else if (!isUploadFlowFinished) {
                        history.push(UPLOAD);
                    } else if (!isAIFlowFinished) {
                        onAIJobsCheck(promoUUID, entryUUID);
                    }
                } else {
                    if (hasGameResult) {
                        if (isNoResultPageGames(promoType)) history.push(JOIN);
                        history.push(RESULT);
                    }
                }

                isInitialized.current = true;
            }
        };
        init();
    }, [
        entryUUID,
        onRedirect,
        onAIJobsCheck,
        hasGameResult,
        isEntryRecordResultInitialized,
        promoUUID,
        history,
        promoType
    ]);

    return (
        <div css={containerStyle}>
            {isShowRetryDialog && (
                <PureDialog
                    open
                    titleText={i18next.t('dialog.ai_retry_dialog.title')}
                    primaryButtonText={i18next.t('dialog.ai_retry_dialog.retry_button_text')}
                    onPrimaryButtonClick={handleRetryButtonClick}
                    isShowCloseIcon={false}
                    titleAlign="center"
                />
            )}
            <div css={contentStyle}>
                {Boolean(enableLoadingImage) &&
                    (isLottieFileUrl ? (
                        <LottieFile
                            url={loadingImageUrl || LOTTIE_FILE_URL.transitionLoading}
                            loop={true}
                            autoPlay={true}
                            style={lottieStyle}
                            {...(isDefaultLottieFile ? { fillColor: titleColor } : {})}
                        />
                    ) : (
                        loadingImageUrl && (
                            <img src={loadingImageUrl} alt="loading" css={css(imageStyle)} />
                        )
                    ))}
                {Boolean(enableAnimationHintText) && Boolean(animationHintText) && (
                    <WaveAnimationText text={animationHintText} color={titleColor} />
                )}
                {Boolean(enableTransitionContent) && Boolean(transitionContent) && (
                    <TextBoxClient
                        style={{
                            backgroundColor: 'transparent'
                        }}
                        content={transitionContent}
                        ignoreContentPadding
                        shouldUseCompressedImg
                    />
                )}
            </div>
        </div>
    );
}

const lottieStyle = {
    maxWidth: '100%',
    maxHeight: '160px'
};

const imageStyle = css`
    max-width: 100%;
`;

const containerStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
`;

const contentStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 16px;
`;
